import React, { useState, useEffect, useCallback } from 'react';
import { Heart, Loader2 } from 'lucide-react';
import { cn } from '@/utils/cn';
import useFavoritos from '@/hooks/useFavoritos';

interface FavoriteButtonProps {
  ticketId: number;
  showText?: boolean;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'outline' | 'ghost';
  className?: string;
  showBorder?: boolean;
  isFavorite?: boolean;
  onToggle?: (isFavorite: boolean) => void;
}

const FavoriteButton: React.FC<FavoriteButtonProps> = React.memo(({
  ticketId,
  showText = false,
  size = 'md',
  variant = 'default',
  className,
  showBorder = true,
  isFavorite: externalIsFavorite,
  onToggle,
}) => {
  // Só usar o hook se não tiver prop externa
  const favoritosHook = externalIsFavorite === undefined ? useFavoritos() : null;
  const [isLoading, setIsLoading] = useState(false);
  const [localIsFavorite, setLocalIsFavorite] = useState(false);

  // Atualizar estado local quando os favoritos mudarem ou quando a prop externa mudar
  useEffect(() => {
    if (externalIsFavorite !== undefined) {
      setLocalIsFavorite(externalIsFavorite);
    } else if (favoritosHook) {
      setLocalIsFavorite(favoritosHook.isFavorito(ticketId));
    }
  }, [favoritosHook, ticketId, externalIsFavorite]);

  const handleToggle = useCallback(async () => {
    if (isLoading) return;

    try {
      setIsLoading(true);
      
      let response;
      if (favoritosHook) {
        // Usar hook se disponível
        response = await favoritosHook.toggleFavorito(ticketId);
      } else {
        // Usar serviço diretamente quando tiver prop externa
        const { FavoritosService } = await import('@/services');
        response = await FavoritosService.toggleFavorito(ticketId);
      }
      
      setLocalIsFavorite(response.favorito);
      
      // Callback opcional para o componente pai
      if (onToggle) {
        onToggle(response.favorito);
      }
    } catch (error) {
      console.error('Erro ao alterar favorito:', error);
      // Reverter o estado local em caso de erro
      setLocalIsFavorite(!localIsFavorite);
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, favoritosHook, ticketId, onToggle, localIsFavorite]);

  const sizeClasses = {
    sm: 'h-8 px-2 text-xs',
    md: 'h-9 px-3 text-sm',
    lg: 'h-10 px-4 text-base',
  };

  const iconSizeClasses = {
    sm: 'h-3 w-3',
    md: 'h-4 w-4',
    lg: 'h-5 w-5',
  };

  const variantClasses = {
    default: localIsFavorite
      ? 'bg-red-500 hover:bg-red-600 text-white border-red-500'
      : 'bg-gray-100 hover:bg-gray-200 text-gray-600 border-gray-300',
    outline: localIsFavorite
      ? 'border-red-500 text-red-500 hover:bg-red-50'
      : 'border-gray-300 text-gray-600 hover:bg-gray-50',
    ghost: localIsFavorite
      ? 'text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 bg-red-50 dark:bg-red-900/20'
      : 'text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 bg-gray-50 dark:bg-gray-800/50',
  };

  return (
    <button
      onClick={handleToggle}
      disabled={isLoading}
      className={cn(
        'inline-flex items-center justify-center gap-2 font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 hover:scale-110 active:scale-95',
        showBorder && 'rounded-lg border',
        !showBorder && 'rounded-lg p-2',
        sizeClasses[size],
        variantClasses[variant],
        className
      )}
      title={localIsFavorite ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
    >
      {isLoading ? (
        <Loader2 className={cn('animate-spin', iconSizeClasses[size])} />
      ) : (
        <Heart
          className={cn(
            iconSizeClasses[size],
            localIsFavorite ? 'fill-current' : ''
          )}
        />
      )}
      
      {showText && (
        <span>
          {isLoading
            ? 'Processando...'
            : localIsFavorite
            ? 'Favorito'
            : 'Favoritar'
          }
        </span>
      )}
    </button>
  );
});

export default FavoriteButton;